import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ClipboardCheck } from 'lucide-react';
import type { IpdTaskOutcome, IpdTaskReasonCode, IpdTaskView } from '@his/shared';
import { tr, translateServerMessage } from '@/lib/i18n-core';
import { post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { timeOnly } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, DateTimePicker, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { givenAtFrom, localDateTimeValue } from '../dose-timing';
import {
  EMPTY_READINGS,
  buildRecordRequest,
  displayStatus,
  needsWitness,
  reasonChoices,
  reasonMode,
  recordOutcomes,
  recordProblems,
  takesDose,
  takesReadings,
  takesSite,
  takesTime,
  witnessNoticeKey,
  type ReadingDraft,
  type RecordField,
  type RecordForm,
  type RecordProblem,
} from './record-task';
import { ReadingsFields } from './ReadingsFields';
import { OrderBadges, TaskStatusChip } from './TaskChip';
import { outcomeLabel, taskTitle } from './task-labels';
import { useWitnesses } from './task-queries';

function problemText(problem: RecordProblem | undefined): string | undefined {
  if (!problem) return undefined;
  if ('server' in problem) return translateServerMessage(problem.server) ?? problem.server;
  return tr('ipd', problem.key, problem.params);
}

export function RecordTaskDialog({
  task,
  patientLabel,
  onClose,
  onSaved,
}: {
  task: IpdTaskView;
  patientLabel?: string;
  onClose: () => void;
  onSaved: (task: IpdTaskView) => void;
}) {
  const toast = useToast();
  const { user } = useAuth();
  const [openedAt] = useState(() => new Date());
  const [outcome, setOutcome] = useState<IpdTaskOutcome>('given');
  const [atValue, setAtValue] = useState(() => localDateTimeValue(openedAt));
  const [reasonCode, setReasonCode] = useState<IpdTaskReasonCode | ''>('');
  const [reasonText, setReasonText] = useState('');
  const [witnessUserId, setWitnessUserId] = useState('');
  const [doseGiven, setDoseGiven] = useState('');
  const [site, setSite] = useState('');
  const [notes, setNotes] = useState('');
  const [readings, setReadings] = useState<ReadingDraft>(EMPTY_READINGS);
  const [submitted, setSubmitted] = useState(false);

  const at = givenAtFrom(atValue, openedAt);
  const form: RecordForm = { outcome, at, reasonCode, reasonText, witnessUserId, doseGiven, site, notes, readings };
  const dose = task.taskKind === 'dose';
  const status = displayStatus(task, openedAt);
  const outcomes = recordOutcomes(task, openedAt);
  const mode = reasonMode(task, outcome, at);
  const choices = reasonChoices(task, outcome, at);
  const problems = recordProblems(task, form, Date.now());
  const errorOf = (field: RecordField) => (submitted ? problemText(problems.find((problem) => problem.field === field)) : undefined);
  const witnessNeeded = needsWitness(task, outcome);
  const witnessNotice = witnessNoticeKey(task);
  const witnesses = useWitnesses(witnessNeeded);
  const candidates = (witnesses.data ?? []).filter((candidate) => candidate.id !== user?.id);
  const dirty = reasonCode !== '' || reasonText !== '' || witnessUserId !== '' || doseGiven !== '' || site !== '' || notes !== '' || readings !== EMPTY_READINGS;

  const save = useMutation({
    mutationFn: () => post<IpdTaskView>(`/ipd/tasks/${task.id}/record`, buildRecordRequest(task, form)),
    onSuccess: (saved) => {
      toast.success(tr('ipd', dose ? 'dose_recorded' : 'io_task_saved'));
      onSaved(saved);
    },
  });

  const chooseOutcome = (next: IpdTaskOutcome) => {
    setOutcome(next);
    setReasonCode('');
  };
  const submit = () => {
    setSubmitted(true);
    if (problems.length === 0) save.mutate();
  };

  const reasonLabel = mode === 'late' ? tr('ipd', 'io_task_late_reason') : tr('ipd', 'io_task_reason');
  const timeLabel = outcome === 'given' ? tr('ipd', dose ? 'io_task_given_at' : 'io_task_done_at') : tr('ipd', 'io_task_time');

  return (
    <Dialog
      open
      onClose={onClose}
      icon={ClipboardCheck}
      title={task.order.label}
      description={tr('ipd', 'io_task_dialog_desc', { task: taskTitle(task), time: timeOnly(task.scheduledAt) })}
      dirty={dirty}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('ipd', 'cancel')}
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            {tr('ipd', 'record')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {patientLabel && <span className="font-medium">{patientLabel}</span>}
          <TaskStatusChip status={status} />
          <OrderBadges priority={task.order.priority} isHighAlert={task.order.isHighAlert} state={task.order.state} />
        </div>

        {witnessNotice && <Alert variant="warning">{tr('ipd', witnessNotice)}</Alert>}

        <Field label={tr('ipd', 'outcome')} required>
          <ChoiceButtons<IpdTaskOutcome>
            value={outcome}
            onChange={chooseOutcome}
            label={tr('ipd', 'outcome')}
            className="flex-wrap [&>button]:min-w-[5.5rem]"
            options={outcomes.map((value) => ({
              value,
              label: outcomeLabel(value, task.taskKind),
              tone: value === 'given' ? 'default' : value === 'held' ? 'warning' : 'destructive',
            }))}
          />
        </Field>

        {outcome === 'missed' && <p className="text-xs text-muted-foreground">{tr('ipd', 'io_task_missed_hint')}</p>}

        {choices.length > 0 && (
          <Field label={reasonLabel} required={mode === 'required'} error={errorOf('reasonCode')} hint={mode === 'late' ? tr('ipd', 'io_task_late_hint') : undefined} hintBelow>
            <ChoiceButtons<IpdTaskReasonCode>
              layout="chips"
              value={reasonCode}
              onChange={(next) => setReasonCode(mode === 'late' && next === reasonCode ? '' : next)}
              label={reasonLabel}
              options={choices.map((code) => ({ value: code, label: tr('ipd', `io_reason_${code}`) }))}
            />
          </Field>
        )}

        {(mode === 'early' || reasonCode === 'other') && (
          <Field
            label={mode === 'early' ? tr('ipd', 'io_task_early_reason') : tr('ipd', 'io_task_other_reason')}
            required
            error={errorOf('reasonText')}
            hint={mode === 'early' ? tr('ipd', 'io_task_early_hint') : undefined}
            hintBelow
          >
            <Input value={reasonText} maxLength={400} onChange={(event) => setReasonText(event.target.value)} />
          </Field>
        )}

        {takesTime(outcome) && (
          <Field label={timeLabel} required error={errorOf('at')}>
            <DateTimePicker className="w-full" value={atValue} onChange={setAtValue} max={localDateTimeValue(new Date())} />
          </Field>
        )}

        {witnessNeeded && (
          <Field label={tr('ipd', 'witnessed_by')} required error={errorOf('witness')}>
            <Select value={witnessUserId} onChange={(event) => setWitnessUserId(event.target.value)}>
              <option value="">{tr('ipd', 'select_the_second_nurse')}</option>
              {candidates.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.designation ? `${candidate.name} · ${candidate.designation}` : candidate.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {witnessNeeded && witnesses.isSuccess && candidates.length === 0 && <p className="text-xs text-destructive">{tr('ipd', 'nobody_else_can_witness_this_dose')}</p>}

        {(takesDose(task, outcome) || takesSite(task, outcome)) && (
          <div className="grid gap-3 sm:grid-cols-2">
            {takesDose(task, outcome) && (
              <Field label={tr('ipd', 'io_task_dose_given')} hint={tr('ipd', 'io_task_dose_given_hint')} hintBelow>
                <Input value={doseGiven} maxLength={80} onChange={(event) => setDoseGiven(event.target.value)} />
              </Field>
            )}
            {takesSite(task, outcome) && (
              <Field label={tr('ipd', 'site')}>
                <Input value={site} maxLength={80} onChange={(event) => setSite(event.target.value)} placeholder={tr('ipd', 'left_forearm_right_deltoid')} />
              </Field>
            )}
          </div>
        )}

        {takesReadings(task, outcome) && <ReadingsFields taskKind={task.taskKind} value={readings} onChange={setReadings} errorOf={errorOf} />}

        <Field label={tr('ipd', 'notes')}>
          <Textarea value={notes} maxLength={1000} rows={2} onChange={(event) => setNotes(event.target.value)} />
        </Field>

        {submitted && problems.length > 0 && <Alert variant="destructive">{tr('ipd', 'io_task_fix_problems')}</Alert>}
        {save.isError && <Alert variant="destructive">{describeError(save.error)}</Alert>}
      </div>
    </Dialog>
  );
}
